import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { Service, NewService } from '../types';

/**
 * PostgREST non conosce `billing_prices` finché la migration
 * 20260813_billing_prices_per_frequenza.sql non è applicata: risponde PGRST204
 * ("could not find the column") oppure 42703 dal database. In quel caso il
 * salvataggio va ritentato senza il campo, invece di far perdere all'utente
 * tutte le altre modifiche al servizio.
 */
function isMissingBillingPrices(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  const code = error.code ?? '';
  const message = error.message ?? '';
  return (code === 'PGRST204' || code === '42703') && message.includes('billing_prices');
}

interface ServiceStore {
  services: Service[];
  loading: boolean;
  error: string | null;
  showArchived: boolean;
  fetchServices: () => Promise<void>;
  fetchServiceById: (id: string) => Promise<Service | null>;
  getService: (id: string) => Promise<Service | null>;
  createService: (service: NewService) => Promise<Service | null>;
  updateService: (id: string, service: Partial<Service>) => Promise<Service | null>;
  deleteService: (id: string) => Promise<boolean>;
  archiveService: (id: string) => Promise<boolean>;
  unarchiveService: (id: string) => Promise<boolean>;
  toggleShowArchived: () => void;
  checkServiceDependencies: (serviceIds: string[]) => Promise<{ serviceId: string; serviceName: string; deals: Array<{ id: string; title: string; }> }[]>;
}

export const useServiceStore = create<ServiceStore>((set, get) => ({
  services: [],
  loading: false,
  error: null,
  showArchived: false,

  fetchServices: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      let query = supabase
        .from('services')
        .select('*, category:category_id (id, name, description, color)')
        .eq('organization_id', profileData.organization_id);

      if (!get().showArchived) {
        query = query.or('archived.is.null,archived.eq.false');
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      set({ services: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  fetchServiceById: async (id: string) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('services')
        .select('*, category:category_id (id, name, description, color)')
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .single();

      if (error) throw error;
      
      // Update services array if service exists
      if (data) {
        const currentServices = get().services;
        const updatedServices = currentServices.some(s => s.id === id)
          ? currentServices.map(s => s.id === id ? data : s)
          : [...currentServices, data];
        set({ services: updatedServices });
      }
      
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  getService: async (id: string) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('services')
        .select('*, category:category_id (id, name, description, color)')
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createService: async (service) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Add organization_id to service data and normalize UUID fields
      const serviceData = {
        ...service,
        organization_id: profileData.organization_id
      };

      // Normalize UUID fields - convert empty strings to null for database compatibility
      const uuidFields = ['category_id', 'default_payment_method_id'];
      
      uuidFields.forEach(field => {
        if (serviceData[field] === '') {
          serviceData[field] = null;
        }
      });
      
      console.log('🔧 Normalizing service create data:', {
        original: service,
        normalized: serviceData,
        uuidFieldsNormalized: uuidFields.filter(field => service[field] === '')
      });

      let { data, error } = await supabase
        .from('services')
        .insert(serviceData)
        .select()
        .single();

      // Rete di sicurezza: dove la migration degli sconti per frequenza non è
      // ancora applicata, PostgREST rifiuta la colonna sconosciuta. Meglio
      // salvare il servizio senza quel campo che perdere tutto il resto.
      if (error && isMissingBillingPrices(error)) {
        console.warn('⚠️ services.billing_prices assente: salvo senza sconti per frequenza');
        const { billing_prices: _dropped, ...withoutBillingPrices } = serviceData;
        ({ data, error } = await supabase
          .from('services')
          .insert(withoutBillingPrices)
          .select()
          .single());
      }

      if (error) throw error;

      const services = get().services;
      set({ services: [data, ...services] });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateService: async (id, service) => {
    try {
      // Get current user's organization_id for security
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Normalize UUID fields - convert empty strings to null for database compatibility
      const normalizedService = { ...service };
      const uuidFields = ['category_id', 'default_payment_method_id'];
      
      uuidFields.forEach(field => {
        if (normalizedService[field] === '') {
          normalizedService[field] = null;
        }
      });
      
      console.log('🔧 Normalizing service update data:', {
        original: service,
        normalized: normalizedService,
        uuidFieldsNormalized: uuidFields.filter(field => service[field] === '')
      });

      let { data, error } = await supabase
        .from('services')
        .update(normalizedService)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .select()
        .single();

      // Come nella create: senza la colonna degli sconti per frequenza si salva
      // comunque il resto del servizio.
      if (error && isMissingBillingPrices(error)) {
        console.warn('⚠️ services.billing_prices assente: salvo senza sconti per frequenza');
        const { billing_prices: _dropped, ...withoutBillingPrices } = normalizedService;
        ({ data, error } = await supabase
          .from('services')
          .update(withoutBillingPrices)
          .eq('id', id)
          .eq('organization_id', profileData.organization_id)
          .select()
          .single());
      }

      if (error) throw error;

      const services = get().services.map(s =>
        s.id === id ? { ...s, ...data } : s
      );
      set({ services });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteService: async (id) => {
    try {
      // Get current user's organization_id for security
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // First, get all deal_services that use this service
      const { data: dealServicesData, error: dealServicesQueryError } = await supabase
        .from('deal_services')
        .select('id')
        .eq('service_id', id);

      if (dealServicesQueryError) throw dealServicesQueryError;

      // Delete variables associated with each deal_service (they have deal_service_id, not service_id)
      if (dealServicesData && dealServicesData.length > 0) {
        const dealServiceIds = dealServicesData.map(ds => ds.id);
        
        const { error: variablesError } = await supabase
          .from('deal_variables')
          .delete()
          .in('deal_service_id', dealServiceIds);

        if (variablesError) {
          console.warn('Warning: Could not delete service variables:', variablesError);
        }
      }

      // Remove all references from deal_services table
      const { error: dealServicesError } = await supabase
        .from('deal_services')
        .delete()
        .eq('service_id', id);

      if (dealServicesError) throw dealServicesError;

      // ALSO handle direct references from deals table
      // Set service_id to NULL for deals that directly reference this service
      const { error: dealsUpdateError } = await supabase
        .from('deals')
        .update({ service_id: null })
        .eq('service_id', id)
        .eq('organization_id', profileData.organization_id);

      if (dealsUpdateError) throw dealsUpdateError;

      // Now delete the service itself
      const { error: serviceError } = await supabase
        .from('services')
        .delete()
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (serviceError) throw serviceError;

      const services = get().services.filter(s => s.id !== id);
      set({ services });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  archiveService: async (id) => {
    const result = await get().updateService(id, { archived: true });
    if (result) {
      if (!get().showArchived) {
        set(state => ({
          services: state.services.filter(s => s.id !== id)
        }));
      } else {
        set(state => ({
          services: state.services.map(s => s.id === id ? { ...s, archived: true } : s)
        }));
      }
      return true;
    }
    return false;
  },

  unarchiveService: async (id) => {
    const result = await get().updateService(id, { archived: false });
    if (result) {
      set(state => ({
        services: state.services.map(s => s.id === id ? { ...s, archived: false } : s)
      }));
      return true;
    }
    return false;
  },

  toggleShowArchived: () => {
    set(state => ({ showArchived: !state.showArchived }));
    get().fetchServices();
  },

  checkServiceDependencies: async (serviceIds) => {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Get services info
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, title')
        .in('id', serviceIds)
        .eq('organization_id', profileData.organization_id);

      if (servicesError) throw servicesError;

      // Check which services are referenced by deals (through deal_services)
      const { data: dealServicesData, error: dealServicesError } = await supabase
        .from('deal_services')
        .select(`
          service_id,
          deals!inner (
            id,
            title,
            organization_id
          )
        `)
        .in('service_id', serviceIds)
        .eq('deals.organization_id', profileData.organization_id);

      if (dealServicesError) throw dealServicesError;

      // Also check direct references from deals table
      const { data: directDealsData, error: directDealsError } = await supabase
        .from('deals')
        .select('id, title, service_id')
        .in('service_id', serviceIds)
        .eq('organization_id', profileData.organization_id);

      if (directDealsError) throw directDealsError;

      // Group by service ID and collect unique deals
      const serviceDealsMap = new Map<string, Array<{ id: string; title: string; }>>();
      
      // Add deals from deal_services
      dealServicesData?.forEach(item => {
        const serviceId = item.service_id;
        const deal = item.deals;
        
        if (!serviceDealsMap.has(serviceId)) {
          serviceDealsMap.set(serviceId, []);
        }
        
        // Avoid duplicates
        const existingDeals = serviceDealsMap.get(serviceId)!;
        if (!existingDeals.find(d => d.id === deal.id)) {
          existingDeals.push({ id: deal.id, title: deal.title });
        }
      });

      // Add direct deals
      directDealsData?.forEach(deal => {
        const serviceId = deal.service_id;
        
        if (!serviceDealsMap.has(serviceId)) {
          serviceDealsMap.set(serviceId, []);
        }
        
        // Avoid duplicates
        const existingDeals = serviceDealsMap.get(serviceId)!;
        if (!existingDeals.find(d => d.id === deal.id)) {
          existingDeals.push({ id: deal.id, title: deal.title });
        }
      });

      // Build result
      const result = servicesData?.map(service => ({
        serviceId: service.id,
        serviceName: service.title,
        deals: serviceDealsMap.get(service.id) || []
      })) || [];

      return result;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return [];
    }
  },
}));